import { Form, Row, Col, DatePicker, InputNumber, Tooltip, Flex } from "antd";
import MultiSelectTag from "../../../components/multi-select-tag";
import dayjs from "dayjs";
import { DATE_FORMAT, ESPTypeOptions } from "../../../constants/common";
import { ISuppliersByNameResult } from "../../../interfaces/supplier";
import { IMasterDataType } from "../../../interfaces/master-data";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";

interface FilterBscOrEspProps {
  dropdownsData: any;
  type?: string;
  tableName?: string;
}

const FilterBscOrEsp: React.FC<FilterBscOrEspProps> = ({ dropdownsData, type, tableName }) => {
  const { currencies = [], suppliers = [], statuses = [] } = dropdownsData || {};

  const renderLabelInfo = (label: string) => {
    return (
      <Flex align="center" gap={5}>
        <span>{label}</span>
        <Tooltip title="Type keywords, separate with commas. Use space (‘ ’) for blank results.">
          <FontAwesomeIcon icon={faInfoCircle} />
        </Tooltip>
      </Flex>
    );
  };

  return (
    <>
      <Row gutter={16}>
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => (
            <>
              <Col span={12}>
                <Form.Item label="Import Date From" name="exportDateFrom">
                  <DatePicker
                    format={DATE_FORMAT}
                    maxDate={dayjs(getFieldValue("exportDateTo"), DATE_FORMAT)}
                    style={{ width: "100%" }}
                    placeholder="From"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Import Date To" name="exportDateTo">
                  <DatePicker
                    format={DATE_FORMAT}
                    minDate={dayjs(getFieldValue("exportDateFrom"), DATE_FORMAT)}
                    style={{ width: "100%" }}
                    placeholder="To"
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </Form.Item>
        {tableName === "expected_supplier_payments" && (
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => (
              <>
                <Col span={12}>
                  <Form.Item label="Export Date From" name="exportedDatetimeFrom">
                    <DatePicker
                      format={DATE_FORMAT}
                      maxDate={dayjs(getFieldValue("exportedDatetimeTo"), DATE_FORMAT)}
                      style={{ width: "100%" }}
                      placeholder="From"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Export Date To" name="exportedDatetimeTo">
                    <DatePicker
                      format={DATE_FORMAT}
                      minDate={dayjs(getFieldValue("exportedDatetimeFrom"), DATE_FORMAT)}
                      style={{ width: "100%" }}
                      placeholder="To"
                    />
                  </Form.Item>
                </Col>
              </>
            )}
          </Form.Item>
        )}
        <Col span={24}>
          <Form.Item label="Supplier Code" name="supplierCodes">
            <MultiSelectTag
              allowClear
              showSearch
              placeholder="All"
              options={suppliers.map((item: ISuppliersByNameResult) => ({
                value: item.code,
                label: `${item.code} - ${item.label}`,
              }))}
              tagDisplay="value"
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item label={renderLabelInfo("Booking Reference")} name="bookingRefs">
            <MultiSelectTag allowClear showSearch placeholder="All" options={[]} mode="tags" />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item label={renderLabelInfo("Lead Pax Name")} name="leadPaxNames">
            <MultiSelectTag allowClear showSearch placeholder="All" options={[]} mode="tags" />
          </Form.Item>
        </Col>

        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => (
            <>
              <Col span={12}>
                <Form.Item label="Component Start Date From" name="componentStartDateFrom">
                  <DatePicker
                    format={DATE_FORMAT}
                    maxDate={dayjs(getFieldValue("componentStartDateTo"), DATE_FORMAT)}
                    style={{ width: "100%" }}
                    placeholder="From"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Component Start Date To" name="componentStartDateTo">
                  <DatePicker
                    format={DATE_FORMAT}
                    minDate={dayjs(getFieldValue("componentStartDateFrom"), DATE_FORMAT)}
                    style={{ width: "100%" }}
                    placeholder="To"
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </Form.Item>
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => (
            <>
              <Col span={12}>
                <Form.Item label="From Local Currency Cost" name="localCurrencyCostFrom">
                  <InputNumber
                    max={getFieldValue("localCurrencyCostTo")}
                    style={{ width: "100%" }}
                    placeholder="From"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="To Local Currency Cost" name="localCurrencyCostTo">
                  <InputNumber
                    min={getFieldValue("localCurrencyCostFrom") || 1}
                    style={{ width: "100%" }}
                    placeholder="To"
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </Form.Item>
        <Col span={24}>
          <Form.Item label="Currency" name="currencies">
            <MultiSelectTag
              allowClear
              showSearch
              placeholder="All"
              options={currencies.map((item: IMasterDataType) => ({
                value: item.code,
                label: `${item.code} - ${item.label}`,
              }))}
              tagDisplay="value"
            />
          </Form.Item>
        </Col>
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => (
            <>
              <Col span={12}>
                <Form.Item label="From No. of Pax (Booking)" name="bookingPaxFrom">
                  <InputNumber
                    min={1}
                    max={getFieldValue("bookingPaxTo")}
                    style={{ width: "100%" }}
                    placeholder="From"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="To No. of Pax (Booking)" name="bookingPaxTo">
                  <InputNumber min={getFieldValue("bookingPaxFrom") || 1} style={{ width: "100%" }} placeholder="To" />
                </Form.Item>
              </Col>
            </>
          )}
        </Form.Item>
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => (
            <>
              <Col span={12}>
                <Form.Item label="Booking Date From" name="bookingDateFrom">
                  <DatePicker
                    format={DATE_FORMAT}
                    maxDate={dayjs(getFieldValue("bookingDateTo"), DATE_FORMAT)}
                    style={{ width: "100%" }}
                    placeholder="From"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Booking Date To" name="bookingDateTo">
                  <DatePicker
                    format={DATE_FORMAT}
                    minDate={dayjs(getFieldValue("bookingDateFrom"), DATE_FORMAT)}
                    style={{ width: "100%" }}
                    placeholder="To"
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </Form.Item>
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => (
            <>
              <Col span={12}>
                <Form.Item label="Departure Date From" name="departureDateFrom">
                  <DatePicker
                    format={DATE_FORMAT}
                    maxDate={dayjs(getFieldValue("departureDateTo"), DATE_FORMAT)}
                    style={{ width: "100%" }}
                    placeholder="From"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Departure Date To" name="departureDateTo">
                  <DatePicker
                    format={DATE_FORMAT}
                    minDate={dayjs(getFieldValue("departureDateFrom"), DATE_FORMAT)}
                    style={{ width: "100%" }}
                    placeholder="To"
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </Form.Item>
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => (
            <>
              <Col span={12}>
                <Form.Item label="From No of Nights" name="numberOfNightsFrom">
                  <InputNumber
                    min={1}
                    max={getFieldValue("numberOfNightsTo")}
                    style={{ width: "100%" }}
                    placeholder="From"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="To No of Nights" name="numberOfNightsTo">
                  <InputNumber
                    min={getFieldValue("numberOfNightsFrom") || 1}
                    style={{ width: "100%" }}
                    placeholder="To"
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </Form.Item>
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => (
            <>
              <Col span={12}>
                <Form.Item label="Component End Date From" name="componentEndDateFrom">
                  <DatePicker
                    format={DATE_FORMAT}
                    maxDate={dayjs(getFieldValue("componentEndDateTo"), DATE_FORMAT)}
                    style={{ width: "100%" }}
                    placeholder="From"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Component End Date To" name="componentEndDateTo">
                  <DatePicker
                    format={DATE_FORMAT}
                    minDate={dayjs(getFieldValue("componentEndDateFrom"), DATE_FORMAT)}
                    style={{ width: "100%" }}
                    placeholder="To"
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </Form.Item>
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => (
            <>
              <Col span={12}>
                <Form.Item label="From GBP Cost" name="gbpCostFrom">
                  <InputNumber max={getFieldValue("gbpCostTo")} style={{ width: "100%" }} placeholder="From" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="To GBP Cost" name="gbpCostTo">
                  <InputNumber min={getFieldValue("gbpCostFrom") || 1} style={{ width: "100%" }} placeholder="To" />
                </Form.Item>
              </Col>
            </>
          )}
        </Form.Item>
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => (
            <>
              <Col span={12}>
                <Form.Item label="From No. of Pax (Supplier)" name="supplierPaxFrom">
                  <InputNumber
                    min={1}
                    max={getFieldValue("supplierPaxTo")}
                    style={{ width: "100%" }}
                    placeholder="From"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="To No. of Pax (Supplier)" name="supplierPaxTo">
                  <InputNumber min={getFieldValue("supplierPaxFrom") || 1} style={{ width: "100%" }} placeholder="To" />
                </Form.Item>
              </Col>
            </>
          )}
        </Form.Item>
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => (
            <>
              <Col span={12}>
                <Form.Item label="From No. of Rooms" name="roomsFrom">
                  <InputNumber min={1} max={getFieldValue("roomsTo")} style={{ width: "100%" }} placeholder="From" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="To No. of Rooms" name="roomsTo">
                  <InputNumber min={getFieldValue("roomsFrom") || 1} style={{ width: "100%" }} placeholder="To" />
                </Form.Item>
              </Col>
            </>
          )}
        </Form.Item>
        <Col span={24}>
          <Form.Item label={renderLabelInfo("Parent Booking Reference")} name="parentBookingRefs">
            <MultiSelectTag allowClear showSearch placeholder="All" options={[]} mode="tags" />
          </Form.Item>
        </Col>
        {type === "ESP" && (
          <Col span={24}>
            <Form.Item label="Workflow" name="types">
              <MultiSelectTag
                allowClear
                showSearch
                placeholder="All"
                options={ESPTypeOptions.map((item: any) => ({
                  value: item?.value || "",
                  label: item?.label || "[Blank]",
                }))}
              />
            </Form.Item>
          </Col>
        )}
        <Col span={24}>
          <Form.Item label="Status" name="statuses">
            <MultiSelectTag
              allowClear
              showSearch
              placeholder="All"
              options={statuses.map((item: string) => ({
                value: item.toString(),
                label: typeof item === "boolean" ? (item ? "Active" : "Inactive") : item,
              }))}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};

export default FilterBscOrEsp;
